import { Box, Container, IconButton, Link, Stack, useTheme, Zoom } from "@mui/material";
import { ClassNames, css, keyframes } from "@emotion/react";
import { Close } from "@mui/icons-material";
import { CSSTransition } from "react-transition-group";
import { FocusScope } from "@react-aria/focus";
import { isInternalRoute, isExternalRoute, isParentRoute } from "./model.js";
import { match } from "ts-pattern";
import { useOverlay, useModal, usePreventScroll } from "@react-aria/overlays";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import NextLink from "next/link";
import React from "react";
const animationCommon = css `
  animation-delay: 0;
  animation-duration: 1.25s;
  animation-fill-mode: both;
  animation-timing-function: cubic-bezier(0.25, 1, 0.3, 1);
  will-change: clip-path;
`;
const enterAnimation = keyframes `
    from {
      clip-path: circle(0% at top right);
    }
    to {
      clip-path: circle(150% at bottom right);
    }
  `;
const exitAnimation = keyframes `
    from {
      clip-path: circle(150% at bottom right);
    }
    to {
      clip-path: circle(0% at top right);
    }
  `;
export const MobileMenu = ({ handleClose, items = [], show }) => {
    const animationRef = React.createRef();
    // preload pages
    const router = useRouter();
    React.useEffect(() => {
        items.filter(isInternalRoute).forEach(({ route }) => router.prefetch(route));
    }, [items, router]);
    return (React.createElement(ClassNames, null, ({ css }) => (React.createElement(CSSTransition, { classNames: {
            enter: `${css(animationCommon)}`,
            enterActive: `${css(animationCommon)} ${css({ animationName: enterAnimation })}`,
            exit: `${css(animationCommon)}`,
            exitActive: `${css(animationCommon)} ${css({ animationName: exitAnimation })}`,
        }, in: show, nodeRef: animationRef, timeout: 1250, unmountOnExit: true },
        React.createElement(Box, { component: "aside", ref: animationRef, sx: {
                background: "#dff0f9",
                height: "100%",
                left: 0,
                position: "fixed",
                top: 0,
                width: "100%",
                zIndex: 100,
            } },
            React.createElement(MobileMenuContent, { animationRef: animationRef, handleClose: handleClose, items: items, show: show }))))));
};
const MobileMenuContent = ({ animationRef, handleClose, items, show }) => {
    const theme = useTheme();
    const overlay = useOverlay({ onClose: handleClose, isOpen: show, isDismissable: true }, animationRef);
    const { t } = useTranslation("c-common");
    useModal({ isDisabled: !show });
    usePreventScroll({ isDisabled: !show });
    const makeParentLinkProps = (an) => match(an)
        .when(isInternalRoute, ({ route }) => ({
        href: route,
        component: Link,
    }))
        .when(isExternalRoute, ({ href }) => ({
        href,
    }))
        .when(isParentRoute, () => {
        throw new Error("Nested parent routes are not allowed.");
    })
        .exhaustive();
    const interpretItem = (an) => match(an)
        .when(isInternalRoute, ({ label, route }) => (React.createElement(Box, { key: `mobile-nav-${label}`, sx: { width: "100%", textAlign: "center" } },
        React.createElement(Link, { component: NextLink, href: route, onClick: handleClose, sx: { pt: 0, pb: 0, width: "100%" }, underline: "none", variant: "h2" }, label))))
        .when(isExternalRoute, ({ href, label }) => (React.createElement(Box, { key: `mobile-nav-${label}`, sx: { width: "100%", textAlign: "center" } },
        React.createElement(Link, { href: href, onClick: handleClose, sx: { pt: 0, pb: 0, width: "100%" }, underline: "none", variant: "h2" }, label))))
        .when(isParentRoute, ({ children, label }) => (React.createElement(Box, { key: `mobile-nav-${label}`, sx: { width: "100%", textAlign: "center" } },
        React.createElement(Link, { onClick: handleClose, sx: { pt: 0, pb: 0, width: "100%" }, underline: "none", variant: "h2", ...makeParentLinkProps(children[0]) }, label),
        React.createElement(Stack, { alignItems: "center", direction: "column", justifyContent: "center", spacing: 1.75, sx: { pt: 3 } }, children.map((child) => match(child)
            .when(isInternalRoute, ({ label, route }) => (React.createElement(Link, { component: NextLink, href: route ?? "/", key: `mobile-nav-child-${child.label}`, onClick: handleClose, sx: { fontWeight: 400, pt: 0, pb: 0, width: "100%" }, underline: "none", variant: "h4" }, label)))
            .when(isExternalRoute, ({ href, label }) => (React.createElement(Link, { href: href, key: `mobile-nav-child-${label}`, onClick: handleClose, sx: { fontWeight: 400, pt: 0, pb: 0, width: "100%" }, underline: "none", variant: "h4" }, label)))
            .when(isParentRoute, () => {
            throw new Error("Nested parent routes are not allowed");
        })
            .exhaustive())))))
        .exhaustive();
    return (React.createElement(FocusScope, { contain: true, autoFocus: true, restoreFocus: true },
        React.createElement(Container, { ...overlay.overlayProps, sx: { height: "100%" } },
            React.createElement(Stack, { alignItems: "flex-start", direction: "row", justifyContent: "flex-end", sx: { pt: { xs: 2, sm: 3 }, pb: 0, width: "100%" } },
                React.createElement(Zoom, { in: show, style: { transitionDelay: `150ms` } },
                    React.createElement(IconButton, { "aria-label": t("close", "Schließen"), name: t("close", "Schließen"), onClick: handleClose, size: "small", sx: { color: theme.palette.text.primary } },
                        React.createElement(Close, { fontSize: "small" })))),
            React.createElement(Stack, { alignItems: "center", direction: "column", justifyContent: "center", spacing: 2.5, sx: { flex: 1, height: "100%", width: "100%" } }, items.map(interpretItem)))));
};
//# sourceMappingURL=mobile-menu.js.map